/**
 * DIKDOC Ministry Executive Command Center
 *
 * National-level dashboard for Ministry of Health, ANAM, CNSS, CNOPS leadership.
 * Shows:
 *  - Real-time activity across all facilities
 *  - Drug utilization trends
 *  - Fraud detection alerts
 *  - Disease epidemiology
 *  - Cost analytics
 *  - Resource planning
 *  - Recalls and shortages
 */

import { useState } from "react";
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  Building2,
  Pill,
  AlertTriangle,
  Banknote,
  ShieldAlert,
  Brain,
  BarChart3,
  MapPin,
  Clock,
} from "lucide-react";

// ============================================================
//  MOCK NATIONAL DATA
// ============================================================

const NATIONAL_KPIS = {
  totalPopulation: 37_400_000,
  totalFacilities: 1_247,
  activePatients: 8_432_000,
  encountersToday: 142_385,
  claimsToday: 87_500,
  reimbursedTodayMad: 18_750_000,
  averageClaimValue: 285,
  fraudAlertsActive: 23,
  recallsActive: 4,
  shortagesActive: 12,
  uptimeSLA: 99.94,
};

const REGIONAL_DATA = [
  { region: "Casablanca-Settat", population: 7_700_000, facilities: 312, encounters30d: 1_240_000, reimbursed30d: 187_000_000 },
  { region: "Rabat-Salé-Kénitra", population: 4_600_000, facilities: 198, encounters30d: 820_000, reimbursed30d: 124_000_000 },
  { region: "Marrakech-Safi", population: 4_500_000, facilities: 165, encounters30d: 612_000, reimbursed30d: 89_000_000 },
  { region: "Fès-Meknès", population: 4_200_000, facilities: 142, encounters30d: 580_000, reimbursed30d: 78_000_000 },
  { region: "Tanger-Tétouan-Al Hoceïma", population: 3_600_000, facilities: 124, encounters30d: 487_000, reimbursed30d: 67_000_000 },
  { region: "Souss-Massa", population: 2_700_000, facilities: 98, encounters30d: 354_000, reimbursed30d: 48_000_000 },
  { region: "Oriental", population: 2_300_000, facilities: 86, encounters30d: 312_000, reimbursed30d: 42_000_000 },
  { region: "Béni Mellal-Khénifra", population: 2_500_000, facilities: 72, encounters30d: 287_000, reimbursed30d: 38_000_000 },
  { region: "Drâa-Tafilalet", population: 1_600_000, facilities: 48, encounters30d: 178_000, reimbursed30d: 22_000_000 },
  { region: "Guelmim-Oued Noun", population: 450_000, facilities: 28, encounters30d: 87_000, reimbursed30d: 11_000_000 },
  { region: "Laâyoune-Sakia El Hamra", population: 380_000, facilities: 22, encounters30d: 65_000, reimbursed30d: 8_500_000 },
  { region: "Dakhla-Oued Ed-Dahab", population: 220_000, facilities: 14, encounters30d: 38_000, reimbursed30d: 4_800_000 },
];

const TOP_DRUGS = [
  { name: "PARACETAMOL 1G", atcCode: "N02BE01", prescriptions30d: 412_500, reimbursedMad: 4_125_000, trend: +2.4 },
  { name: "AMOXICILLINE 500MG", atcCode: "J01CA04", prescriptions30d: 287_300, reimbursedMad: 8_619_000, trend: +5.1 },
  { name: "METFORMINE 1G", atcCode: "A10BA02", prescriptions30d: 198_400, reimbursedMad: 5_952_000, trend: +8.7 },
  { name: "AMLODIPINE 5MG", atcCode: "C08CA01", prescriptions30d: 178_900, reimbursedMad: 5_367_000, trend: -1.2 },
  { name: "ATORVASTATINE 20MG", atcCode: "C10AA05", prescriptions30d: 142_700, reimbursedMad: 7_135_000, trend: +12.3 },
  { name: "RAMIPRIL 5MG", atcCode: "C09AA05", prescriptions30d: 134_200, reimbursedMad: 4_026_000, trend: +3.8 },
  { name: "SALBUTAMOL", atcCode: "R03AC02", prescriptions30d: 98_600, reimbursedMad: 2_958_000, trend: -2.1 },
  { name: "OMEPRAZOLE 20MG", atcCode: "A02BC01", prescriptions30d: 87_300, reimbursedMad: 2_619_000, trend: +1.4 },
];

const FRAUD_ALERTS = [
  { id: "F-2026-001", type: "Duplicate acts", severity: "high", facility: "FAC-CAS-042", subject: "CIN BK123456", amount: 1200, detectedAt: "2026-06-25T04:30Z" },
  { id: "F-2026-002", type: "Ghost patient", severity: "critical", facility: "FAC-RAB-018", subject: "CIN XX999999", amount: 850, detectedAt: "2026-06-25T05:12Z" },
  { id: "F-2026-003", type: "Provider outlier", severity: "high", facility: "FAC-MAR-007", subject: "INPE-DR-8923", amount: 24500, detectedAt: "2026-06-25T03:48Z" },
  { id: "F-2026-004", type: "Impossible chronology", severity: "medium", facility: "FAC-FES-022", subject: "INPE-DR-4512", amount: 720, detectedAt: "2026-06-25T05:55Z" },
];

const RECALLS = [
  { amm: "MA-VALPROATE-500", reason: "Risque hépatotoxicité", severity: "class_i", batch: "B-2026-04", affected: 18450, initiatedBy: "AMMPS" },
  { amm: "MA-RANITIDINE-150", reason: "Contamination NDMA", severity: "class_ii", batch: "B-2026-02", affected: 87200, initiatedBy: "Manufacturer" },
];

const EPIDEMIOLOGY = [
  { disease: "Diabète type 2", code: "E11", newCases30d: 8420, prevalence: 9.4, trend: +3.2 },
  { disease: "Hypertension", code: "I10", newCases30d: 12_400, prevalence: 31.0, trend: +1.8 },
  { disease: "Asthme", code: "J45", newCases30d: 3_120, prevalence: 6.2, trend: -0.4 },
  { disease: "BPCO", code: "J44", newCases30d: 1_870, prevalence: 3.8, trend: +2.1 },
  { disease: "Cancer du sein", code: "C50", newCases30d: 482, prevalence: 0.7, trend: +5.4 },
  { disease: "AVC", code: "I63", newCases30d: 1240, prevalence: 1.2, trend: -1.5 },
];

// ============================================================
//  COMPONENT
// ============================================================

export function MinistryCommandCenter() {
  const [view, setView] = useState<"overview" | "regions" | "drugs" | "fraud" | "epidemiology" | "recalls">("overview");

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <header className="rounded-2xl bg-gradient-to-br from-purple-900/40 via-blue-900/40 to-emerald-900/30 p-6 ring-1 ring-white/10">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-success/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success">
                National Command Center · v10.0
              </span>
              <span className="text-xs text-muted-foreground">Ministère de la Santé · ANAM · CNSS · CNOPS</span>
            </div>
            <h1 className="mt-2 text-2xl font-light">Vue Nationale — Plateforme Souveraine de Santé Numérique</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {NATIONAL_KPIS.activePatients.toLocaleString("fr-MA")} patients actifs · {NATIONAL_KPIS.totalFacilities} établissements · {NATIONAL_KPIS.uptimeSLA}% uptime
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs">
            <span className="h-2 w-2 rounded-full bg-success" />
            <span>Tous systèmes opérationnels</span>
          </div>
        </div>

        {/* Tab nav */}
        <div className="mt-6 flex gap-1 overflow-x-auto rounded-full bg-black/30 p-1 text-xs">
          {[
            { id: "overview", label: "Vue d'ensemble", icon: BarChart3 },
            { id: "regions", label: "Régions", icon: MapPin },
            { id: "drugs", label: "Médicaments", icon: Pill },
            { id: "fraud", label: "Fraude", icon: ShieldAlert },
            { id: "epidemiology", label: "Épidémiologie", icon: Activity },
            { id: "recalls", label: "Rappels", icon: AlertTriangle },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setView(t.id as typeof view)}
                className={
                  "flex items-center gap-2 rounded-full px-4 py-1.5 transition " +
                  (view === t.id
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-white/5")
                }
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </header>

      {view === "overview" && <OverviewView />}
      {view === "regions" && <RegionsView />}
      {view === "drugs" && <DrugsView />}
      {view === "fraud" && <FraudView />}
      {view === "epidemiology" && <EpidemiologyView />}
      {view === "recalls" && <RecallsView />}
    </div>
  );
}

function OverviewView() {
  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KPI label="Population active" value={`${(NATIONAL_KPIS.activePatients / 1_000_000).toFixed(1)}M`} delta="+1.2% / mois" icon={Users} />
        <KPI label="Établissements connectés" value={NATIONAL_KPIS.totalFacilities.toLocaleString("fr-MA")} delta="+12 cette semaine" icon={Building2} />
        <KPI label="Rencontres aujourd'hui" value={NATIONAL_KPIS.encountersToday.toLocaleString("fr-MA")} delta="+8% vs hier" icon={Activity} />
        <KPI label="Remboursement jour (MAD)" value={`${(NATIONAL_KPIS.reimbursedTodayMad / 1_000_000).toFixed(2)}M`} delta="+5.4%" icon={Banknote} />
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <AlertCard
          title="Alertes fraude actives"
          value={NATIONAL_KPIS.fraudAlertsActive}
          severity="danger"
          description="Détection temps réel sur 6 règles + Benford"
          icon={ShieldAlert}
        />
        <AlertCard
          title="Rappels médicaments"
          value={NATIONAL_KPIS.recallsActive}
          severity="warning"
          description="Class I & II prioritaires"
          icon={AlertTriangle}
        />
        <AlertCard
          title="Ruptures de stock"
          value={NATIONAL_KPIS.shortagesActive}
          severity="warning"
          description="Substituts proposés automatiquement"
          icon={Pill}
        />
      </div>

      {/* Live activity */}
      <section>
        <h2 className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">Activité en temps réel</h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <LiveFeed title="Rencontres" icon={Activity} items={[
            { time: "il y a 2 min", text: "Consultation Dr. Bennani @ CHU Casablanca — Patient BK456789", type: "encounter" },
            { time: "il y a 3 min", text: "Urgence @ Hôpital Avicenne Rabat — Patient BK890123", type: "encounter" },
            { time: "il y a 5 min", text: "Téléconsultation Dr. Tazi @ Cabinet Fès — Patient BK234567", type: "encounter" },
          ]} />
          <LiveFeed title="Prescriptions" icon={Pill} items={[
            { time: "il y a 1 min", text: "Amlodipine 10mg — Pharmacie Centrale Casablanca", type: "prescription" },
            { time: "il y a 4 min", text: "Metformine 1g + Paracétamol — Pharmacie Maarif", type: "prescription" },
            { time: "il y a 7 min", text: "Atorvastatine 20mg — Pharmacie Agdal Rabat", type: "prescription" },
          ]} />
          <LiveFeed title="Imagerie IA" icon={Brain} items={[
            { time: "il y a 3 min", text: "CT Thorax — IA: nodule bénin probable (94%)", type: "imaging" },
            { time: "il y a 6 min", text: "IRM Cérébrale — IA: pas d'anomalie structurelle (98%)", type: "imaging" },
            { time: "il y a 12 min", text: "Mammographie — IA: BI-RADS 2 (99%)", type: "imaging" },
          ]} />
        </div>
      </section>
    </div>
  );
}

function RegionsView() {
  return (
    <div className="space-y-4">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Activité par région (30 derniers jours)</h2>
      <div className="card-surface overflow-hidden rounded-2xl">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3 text-left">Région</th>
              <th className="p-3 text-right">Population</th>
              <th className="p-3 text-right">Établissements</th>
              <th className="p-3 text-right">Rencontres 30j</th>
              <th className="p-3 text-right">Remboursement 30j (MAD)</th>
              <th className="p-3 text-right">MAD / habitant</th>
            </tr>
          </thead>
          <tbody>
            {REGIONAL_DATA.map((r) => (
              <tr key={r.region} className="border-t border-white/5">
                <td className="p-3 font-medium">{r.region}</td>
                <td className="p-3 text-right">{r.population.toLocaleString("fr-MA")}</td>
                <td className="p-3 text-right">{r.facilities}</td>
                <td className="p-3 text-right">{r.encounters30d.toLocaleString("fr-MA")}</td>
                <td className="p-3 text-right font-mono">{r.reimbursed30d.toLocaleString("fr-MA")}</td>
                <td className="p-3 text-right">{(r.reimbursed30d / r.population).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DrugsView() {
  return (
    <div className="space-y-4">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Top médicaments (30 derniers jours)</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {TOP_DRUGS.map((d) => (
          <div key={d.atcCode} className="card-surface rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium">{d.name}</div>
                <div className="text-xs text-muted-foreground">ATC {d.atcCode}</div>
              </div>
              <div className={"flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium " + (d.trend > 0 ? "bg-success/15 text-success" : "bg-accent/15 text-accent")}>
                {d.trend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {d.trend > 0 ? "+" : ""}{d.trend}%
              </div>
            </div>
            <div className="mt-3 flex justify-between text-xs text-muted-foreground">
              <span>{d.prescriptions30d.toLocaleString("fr-MA")} prescriptions</span>
              <span className="font-mono">{d.reimbursedMad.toLocaleString("fr-MA")} MAD</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FraudView() {
  return (
    <div className="space-y-4">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Alertes fraude — détection temps réel</h2>
      <div className="grid grid-cols-1 gap-3">
        {FRAUD_ALERTS.map((a) => (
          <div key={a.id} className="card-surface flex items-center justify-between gap-4 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <span className={"h-2.5 w-2.5 rounded-full " + (a.severity === "critical" ? "bg-accent" : a.severity === "high" ? "bg-warning" : "bg-muted")} />
              <div>
                <div className="font-medium">{a.type}</div>
                <div className="text-xs text-muted-foreground">
                  {a.facility} · {a.subject} · {a.detectedAt}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm">{a.amount.toLocaleString("fr-MA")} MAD</div>
              <div className={"text-xs uppercase " + (a.severity === "critical" ? "text-accent" : "text-warning")}>
                {a.severity}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EpidemiologyView() {
  return (
    <div className="space-y-4">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Surveillance épidémiologique</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {EPIDEMIOLOGY.map((e) => (
          <div key={e.code} className="card-surface rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">ICD-10 {e.code}</div>
            <div className="mt-1 text-lg font-medium">{e.disease}</div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Nouveaux cas 30j</div>
                <div className="font-mono">{e.newCases30d.toLocaleString("fr-MA")}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Prévalence</div>
                <div className="font-mono">{e.prevalence}%</div>
              </div>
              <div className={"flex items-center gap-1 rounded-md px-2 py-0.5 text-xs " + (e.trend > 0 ? "bg-accent/15 text-accent" : "bg-success/15 text-success")}>
                {e.trend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {e.trend > 0 ? "+" : ""}{e.trend}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecallsView() {
  return (
    <div className="space-y-4">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Rappels médicaments actifs</h2>
      <div className="grid grid-cols-1 gap-3">
        {RECALLS.map((r) => (
          <div key={r.amm} className={"card-surface flex items-center justify-between gap-4 rounded-xl border p-4 " + (r.severity === "class_i" ? "border-accent/40" : "border-warning/40")}>
            <div>
              <div className="font-medium">{r.amm}</div>
              <div className="text-sm text-muted-foreground">{r.reason}</div>
              <div className="mt-1 text-xs text-muted-foreground">Batch {r.batch} · {r.initiatedBy}</div>
            </div>
            <div className="text-right">
              <div className={"text-xs uppercase font-bold " + (r.severity === "class_i" ? "text-accent" : "text-warning")}>
                {r.severity.replace("_", " ")}
              </div>
              <div className="mt-1 font-mono text-sm">{r.affected.toLocaleString("fr-MA")} unités</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
//  SMALL COMPONENTS
// ============================================================

function KPI({ label, value, delta, icon: Icon }: { label: string; value: string; delta: string; icon: any }) {
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
      </div>
      <div className="mt-3 text-3xl font-light tracking-tight">{value}</div>
      <div className="mt-1 text-xs text-success">{delta}</div>
    </div>
  );
}

function AlertCard({ title, value, severity, description, icon: Icon }: { title: string; value: number; severity: "danger" | "warning"; description: string; icon: any }) {
  const colors = severity === "danger" ? "border-accent/40 text-accent" : "border-warning/40 text-warning";
  return (
    <div className={`card-surface rounded-2xl border p-5 ${colors}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{title}</div>
          <div className="mt-3 text-4xl font-light tracking-tight">{value}</div>
          <div className="mt-1 text-xs text-muted-foreground">{description}</div>
        </div>
        <Icon className="h-6 w-6" strokeWidth={1.6} />
      </div>
    </div>
  );
}

function LiveFeed({ title, icon: Icon, items }: { title: string; icon: any; items: Array<{ time: string; text: string; type: string }> }) {
  return (
    <div className="card-surface rounded-2xl p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">{title}</h3>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <Clock className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={2} />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-muted-foreground">{item.time}</div>
              <div>{item.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MinistryCommandCenter;